import * as THREE from 'three';
import type { Game, GameContext, GameMeta, InputState } from '../types';
import { playExplosion, playPowerup, playShoot } from '../../audio/sfx';
import {
  createThreeKit,
  disposeObject,
  makeGlowDisc,
  makeStars,
  ParticleSystem,
  renderThree,
  resizeThree,
  type ThreeKit,
} from '../shared/threeKit';

type EnemyKind = 'paywall' | 'algo' | 'club';

type Enemy = {
  group: THREE.Group;
  kind: EnemyKind;
  hp: number;
  z: number;
  lane: number;
  height: number;
  radius: number;
  score: number;
  phase: number;
  shotAt: number;
  speedMul: number;
};

type Hazard = {
  mesh: THREE.Object3D;
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  radius: number;
  damage: number;
};

type Beam = {
  line: THREE.Line;
  life: number;
};

type CorridorSegment = {
  group: THREE.Group;
  lamps: THREE.Mesh[];
  rails: THREE.Mesh[];
  z: number;
};

export class VelvetRopeRiotGame implements Game {
  readonly meta: GameMeta = {
    id: 'velvet',
    title: 'VELVET ROPE RIOT',
    blurb: 'On-rails neon shooter. Smash paywalls, dodge gatekeepers, surf the beat.',
    tag: 'SHOOTER',
    status: 'playable',
  };

  private ctx!: GameContext;
  private kit!: ThreeKit;
  private stars!: THREE.Points;
  private reticle!: THREE.Group;
  private particles!: ParticleSystem;
  private corridor: CorridorSegment[] = [];
  private enemies: Enemy[] = [];
  private hazards: Hazard[] = [];
  private beams: Beam[] = [];

  private score = 0;
  private lives = 4;
  private wave = 1;
  private combo = 0;
  private comboTimer = 0;
  private message = 'DRAG / MOVE TO AIM · HOLD FIRE';
  private messageT = 2.8;
  private invuln = 0;
  private fireCd = 0;
  private spawnCd = 0.55;
  private burstCd = 0.9;
  private distance = 0;
  private disposed = false;

  private aimX = 0;
  private aimY = 0;
  private pointerAimX = 0;
  private pointerAimY = 0;
  private pointerRecent = 0;
  private pointerFire = false;
  private pointerFirePressed = false;
  private pointerId: number | null = null;

  private camX = 0;
  private camY = 1.8;

  private readonly tmpVec2 = new THREE.Vector2();
  private readonly tmpVec3 = new THREE.Vector3();
  private readonly tmpVec3B = new THREE.Vector3();
  private readonly tmpSphere = new THREE.Sphere();
  private readonly raycaster = new THREE.Raycaster();

  private onPointerDown = (e: PointerEvent) => {
    if (e.button !== 0 && e.pointerType !== 'touch') return;
    this.pointerId = e.pointerId;
    this.pointerFire = true;
    this.pointerFirePressed = true;
    this.updatePointerAim(e);
    this.kit.renderer.domElement.setPointerCapture(e.pointerId);
  };

  private onPointerMove = (e: PointerEvent) => {
    if (this.pointerId !== null && e.pointerId !== this.pointerId && e.pointerType === 'touch') return;
    this.updatePointerAim(e);
  };

  private onPointerUp = (e: PointerEvent) => {
    if (this.pointerId !== null && e.pointerId !== this.pointerId) return;
    const el = this.kit?.renderer.domElement;
    if (this.pointerId !== null && el && el.hasPointerCapture(this.pointerId)) {
      el.releasePointerCapture(this.pointerId);
    }
    this.pointerFire = false;
    this.pointerId = null;
  };

  async mount(ctx: GameContext): Promise<void> {
    this.ctx = ctx;
    this.kit = createThreeKit(ctx, {
      label: 'Velvet Rope Riot',
      clear: 0x050814,
      fog: 0x050814,
      fogNear: 18,
      fogFar: 60,
      fov: 54,
      z: 20,
    });
    this.kit.scene.add(new THREE.HemisphereLight(0x6de8ff, 0x120818, 0.8));
    const magenta = new THREE.PointLight(0xff4dd3, 1.6, 48);
    magenta.position.set(0, 7, 10);
    this.kit.scene.add(magenta);
    const cyan = new THREE.PointLight(0x33f0ff, 1.2, 54);
    cyan.position.set(0, 4, 26);
    this.kit.scene.add(cyan);

    this.stars = makeStars(this.kit.lowPower ? 70 : 150, 0x8fcbff);
    this.kit.scene.add(this.stars);

    this.buildCorridor();
    this.buildReticle();

    this.particles = new ParticleSystem(this.kit.lowPower ? 320 : 700, 0xff78c6);
    this.kit.root.add(this.particles.points);

    const el = this.kit.renderer.domElement;
    el.style.touchAction = 'none';
    el.addEventListener('pointerdown', this.onPointerDown);
    el.addEventListener('pointermove', this.onPointerMove);
    el.addEventListener('pointerup', this.onPointerUp);
    el.addEventListener('pointercancel', this.onPointerUp);

    const rect = ctx.mount.getBoundingClientRect();
    this.resize(rect.width || 320, rect.height || 480);
  }

  private updatePointerAim(e: PointerEvent): void {
    const rect = this.kit.renderer.domElement.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    this.pointerAimX = THREE.MathUtils.clamp(((e.clientX - rect.left) / rect.width) * 2 - 1, -0.92, 0.92);
    this.pointerAimY = THREE.MathUtils.clamp(1 - ((e.clientY - rect.top) / rect.height) * 2, -0.88, 0.88);
    this.pointerRecent = 2.4;
  }

  private buildCorridor(): void {
    const count = this.kit.lowPower ? 10 : 14;
    const spacing = 5.5;
    for (let i = 0; i < count; i++) {
      const segment = new THREE.Group();

      const floor = new THREE.Mesh(
        new THREE.BoxGeometry(15.5, 0.18, spacing),
        new THREE.MeshStandardMaterial({
          color: 0x08111f,
          emissive: 0x07162b,
          emissiveIntensity: 0.35,
          roughness: 0.9,
          metalness: 0.15,
        }),
      );
      floor.position.y = -2.2;
      segment.add(floor);

      const laneMat = new THREE.MeshBasicMaterial({
        color: 0x41f3ff,
        transparent: true,
        opacity: 0.45,
      });
      for (const laneX of [-4, 0, 4]) {
        const lane = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.01, spacing * 0.9), laneMat.clone());
        lane.position.set(laneX, -2.08, 0);
        segment.add(lane);
      }

      const rails: THREE.Mesh[] = [];
      const lamps: THREE.Mesh[] = [];
      for (const side of [-1, 1] as const) {
        const rail = new THREE.Mesh(
          new THREE.BoxGeometry(0.2, 3.1, spacing),
          new THREE.MeshStandardMaterial({
            color: side < 0 ? 0x1c1030 : 0x0c2133,
            emissive: side < 0 ? 0x5a1e77 : 0x0d5a66,
            emissiveIntensity: 0.5,
            roughness: 0.45,
            metalness: 0.45,
            transparent: true,
            opacity: 0.9,
          }),
        );
        rail.position.set(side * 6.7, -0.7, 0);
        segment.add(rail);
        rails.push(rail);

        for (let j = 0; j < 2; j++) {
          const lamp = new THREE.Mesh(
            new THREE.CylinderGeometry(0.18, 0.18, 0.8, 12),
            new THREE.MeshStandardMaterial({
              color: j === 0 ? 0xff67cb : 0x3df4ff,
              emissive: j === 0 ? 0x822655 : 0x117388,
              emissiveIntensity: 0.85,
              roughness: 0.35,
            }),
          );
          lamp.position.set(side * 6.1, 1.5 + j * 1.35, j === 0 ? -1.6 : 1.6);
          segment.add(lamp);
          lamps.push(lamp);
        }

        const arch = new THREE.Mesh(
          new THREE.BoxGeometry(0.16, 3.9, 0.18),
          new THREE.MeshStandardMaterial({
            color: 0x141124,
            emissive: side < 0 ? 0x48145e : 0x0e4d6d,
            emissiveIntensity: 0.55,
            roughness: 0.5,
          }),
        );
        arch.position.set(side * 4.9, 0.4, 0);
        segment.add(arch);
      }

      const topBar = new THREE.Mesh(
        new THREE.BoxGeometry(10, 0.18, 0.22),
        new THREE.MeshStandardMaterial({
          color: 0x121426,
          emissive: 0x3e1c65,
          emissiveIntensity: 0.65,
          roughness: 0.35,
        }),
      );
      topBar.position.set(0, 2.35, 0);
      segment.add(topBar);

      const halo = makeGlowDisc(i % 2 === 0 ? 0xff4dd3 : 0x3df4ff, 1.6, 0.22);
      halo.position.set(0, 1.2, -spacing * 0.22);
      segment.add(halo);

      const sign = new THREE.Mesh(
        new THREE.BoxGeometry(2.4, 0.8, 0.14),
        new THREE.MeshStandardMaterial({
          color: 0x221230,
          emissive: i % 2 === 0 ? 0x6e1f7e : 0x0f5f76,
          emissiveIntensity: 0.72,
          roughness: 0.35,
          metalness: 0.3,
        }),
      );
      sign.position.set(0, 1.55, 0.05);
      segment.add(sign);

      segment.position.z = i * spacing + 4;
      this.kit.root.add(segment);
      this.corridor.push({ group: segment, lamps, rails, z: segment.position.z });
    }
  }

  private buildReticle(): void {
    this.reticle = new THREE.Group();

    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(0.28, 0.04, 8, 24),
      new THREE.MeshBasicMaterial({ color: 0xfdf5ff, transparent: true, opacity: 0.95 }),
    );
    this.reticle.add(ring);

    const dot = new THREE.Mesh(
      new THREE.SphereGeometry(0.05, 8, 8),
      new THREE.MeshBasicMaterial({ color: 0x3df4ff }),
    );
    this.reticle.add(dot);

    const glow = makeGlowDisc(0xff67cb, 0.45, 0.2);
    this.reticle.add(glow);

    this.kit.scene.add(this.reticle);
  }

  private spawnEnemy(kindBias: number, bass: number): void {
    const roll = Math.random();
    const kind: EnemyKind =
      roll < 0.34 + bass * 0.15
        ? 'paywall'
        : roll < 0.68 + kindBias * 0.12
          ? 'algo'
          : 'club';
    const lane = Math.floor(Math.random() * 5) - 2;
    const height = kind === 'algo' ? 1.5 + Math.random() * 2.1 : 0.2 + Math.random() * 2.2;
    const enemy = this.makeEnemy(kind, lane, height);
    enemy.z = 30 + Math.random() * 18;
    enemy.group.position.z = enemy.z;
    this.enemies.push(enemy);
    this.kit.root.add(enemy.group);
  }

  private makeEnemy(kind: EnemyKind, lane: number, height: number): Enemy {
    const group = new THREE.Group();
    const phase = Math.random() * Math.PI * 2;
    let hp = 1;
    let radius = 0.9;
    let score = 50;
    let speedMul = 1;

    if (kind === 'paywall') {
      hp = 3;
      radius = 1.15;
      score = 110;
      speedMul = 0.85;

      const frame = new THREE.Mesh(
        new THREE.BoxGeometry(1.8, 2.4, 0.5),
        new THREE.MeshStandardMaterial({
          color: 0x201324,
          emissive: 0x6d2043,
          emissiveIntensity: 0.72,
          roughness: 0.45,
          metalness: 0.28,
        }),
      );
      group.add(frame);

      const slot = new THREE.Mesh(
        new THREE.BoxGeometry(0.9, 1.2, 0.08),
        new THREE.MeshBasicMaterial({ color: 0xffec87, transparent: true, opacity: 0.88 }),
      );
      slot.position.z = 0.29;
      group.add(slot);

      const shackle = new THREE.Mesh(
        new THREE.TorusGeometry(0.32, 0.08, 8, 20),
        new THREE.MeshStandardMaterial({
          color: 0xff82cc,
          emissive: 0x7a2b59,
          emissiveIntensity: 0.7,
          metalness: 0.45,
          roughness: 0.25,
        }),
      );
      shackle.position.set(0, 0.52, 0.32);
      group.add(shackle);
      group.add(makeGlowDisc(0xff5cb8, 1.35, 0.25));
    } else if (kind === 'algo') {
      hp = 2;
      radius = 0.95;
      score = 90;

      const eye = new THREE.Mesh(
        new THREE.IcosahedronGeometry(0.75, 0),
        new THREE.MeshStandardMaterial({
          color: 0x4ff0ff,
          emissive: 0x13626e,
          emissiveIntensity: 0.9,
          roughness: 0.25,
          metalness: 0.35,
        }),
      );
      group.add(eye);

      for (let i = 0; i < 2; i++) {
        const ring = new THREE.Mesh(
          new THREE.TorusGeometry(1 + i * 0.28, 0.05, 8, 28),
          new THREE.MeshBasicMaterial({
            color: i === 0 ? 0x8ffbff : 0xff6fcb,
            transparent: true,
            opacity: 0.7 - i * 0.15,
          }),
        );
        ring.rotation.x = i === 0 ? 0.7 : -0.8;
        group.add(ring);
      }
      const pupil = new THREE.Mesh(
        new THREE.SphereGeometry(0.22, 10, 10),
        new THREE.MeshBasicMaterial({ color: 0x090c14 }),
      );
      pupil.position.z = 0.58;
      group.add(pupil);
    } else {
      hp = 2;
      radius = 1;
      score = 95;

      const bot = new THREE.Group();
      const body = new THREE.Mesh(
        new THREE.CapsuleGeometry(0.38, 1.05, 4, 8),
        new THREE.MeshStandardMaterial({
          color: 0xffd067,
          emissive: 0x6f4412,
          emissiveIntensity: 0.75,
          roughness: 0.35,
          metalness: 0.2,
        }),
      );
      bot.add(body);

      const rope = new THREE.Mesh(
        new THREE.TorusGeometry(0.85, 0.07, 8, 22),
        new THREE.MeshBasicMaterial({ color: 0xff67cb, transparent: true, opacity: 0.82 }),
      );
      rope.rotation.x = Math.PI / 2;
      rope.position.y = -0.1;
      bot.add(rope);

      const visor = new THREE.Mesh(
        new THREE.BoxGeometry(0.72, 0.18, 0.22),
        new THREE.MeshBasicMaterial({ color: 0x15161f }),
      );
      visor.position.set(0, 0.18, 0.38);
      bot.add(visor);

      group.add(bot);
      group.add(makeGlowDisc(0xffd067, 1.2, 0.22));
    }

    group.position.set(lane * 2.5, height, 0);
    return {
      group,
      kind,
      hp,
      z: 0,
      lane,
      height,
      radius,
      score,
      phase,
      shotAt: 17 + Math.random() * 9,
      speedMul,
    };
  }

  private spawnHazard(enemy: Enemy, speed: number): void {
    const start = enemy.group.position;
    const targetX = this.camX + Math.sin(this.distance * 0.06 + enemy.lane) * 0.6;
    const targetY = 1.1 + Math.random() * 1.2;
    const travel = Math.max(0.45, enemy.z / (speed + 16));
    const vx = (targetX - start.x) / travel;
    const vy = (targetY - start.y) / travel;
    const vz = -enemy.z / travel;
    const color =
      enemy.kind === 'paywall' ? 0xff7aa2 : enemy.kind === 'algo' ? 0x71f2ff : 0xffd067;

    const core = new THREE.Mesh(
      new THREE.OctahedronGeometry(enemy.kind === 'paywall' ? 0.34 : 0.28, 0),
      new THREE.MeshStandardMaterial({
        color,
        emissive: color,
        emissiveIntensity: 0.85,
        roughness: 0.2,
      }),
    );
    core.position.copy(start);
    this.kit.root.add(core);
    this.hazards.push({
      mesh: core,
      x: start.x,
      y: start.y,
      z: enemy.z,
      vx,
      vy,
      vz,
      radius: enemy.kind === 'paywall' ? 0.65 : 0.5,
      damage: enemy.kind === 'paywall' ? 2 : 1,
    });
  }

  private updateAim(dt: number, input: InputState): void {
    this.pointerRecent = Math.max(0, this.pointerRecent - dt);
    if (this.pointerRecent > 0) {
      this.aimX += (this.pointerAimX - this.aimX) * Math.min(1, dt * 14);
      this.aimY += (this.pointerAimY - this.aimY) * Math.min(1, dt * 14);
    } else {
      const dx = (input.right ? 1 : 0) - (input.left ? 1 : 0);
      const dy = (input.up ? 1 : 0) - (input.down ? 1 : 0);
      const aimSpeed = 1.85;
      this.aimX = THREE.MathUtils.clamp(this.aimX + dx * dt * aimSpeed, -0.92, 0.92);
      this.aimY = THREE.MathUtils.clamp(this.aimY + dy * dt * aimSpeed, -0.88, 0.88);
    }
  }

  private updateCamera(dt: number, energy: number, kick: number): void {
    const routeX =
      Math.sin(this.distance * 0.054) * 2.9 +
      Math.sin(this.distance * 0.018 + 1.3) * 1.7 +
      kick * 0.6;
    const routeY = 1.8 + Math.sin(this.distance * 0.032 + 0.4) * 0.2 + energy * 0.18;
    this.camX += (routeX - this.camX) * Math.min(1, dt * 2.2);
    this.camY += (routeY - this.camY) * Math.min(1, dt * 2.4);

    const camera = this.kit.camera as THREE.PerspectiveCamera;
    camera.position.set(this.camX, this.camY, 0);
    camera.lookAt(this.camX * 0.3 + this.aimX * 1.6, 0.7 + this.aimY * 0.9, 10.5);
  }

  private updateCorridor(dt: number, speed: number, beat: number, highs: number): void {
    const spacing = 5.5;
    const total = spacing * this.corridor.length;
    for (const segment of this.corridor) {
      segment.z -= speed * dt;
      if (segment.z < -spacing - 3) segment.z += total;
      segment.group.position.z = segment.z;
      segment.group.position.x = Math.sin((segment.z + this.distance) * 0.03) * 0.8;
      const pulse = 0.45 + beat * 0.65 + highs * 0.22;
      segment.lamps.forEach((lamp, index) => {
        lamp.scale.setScalar(1 + pulse * (index === 0 ? 0.12 : 0.08));
        const mat = lamp.material as THREE.MeshStandardMaterial;
        mat.emissiveIntensity = 0.65 + pulse * 0.45;
      });
      segment.rails.forEach((rail, index) => {
        const mat = rail.material as THREE.MeshStandardMaterial;
        mat.emissiveIntensity = 0.45 + pulse * (index === 0 ? 0.35 : 0.25);
      });
    }
  }

  private refreshReticle(beat: number): void {
    this.tmpVec3.set(this.aimX, this.aimY, 0.25).unproject(this.kit.camera);
    this.reticle.position.copy(this.tmpVec3);
    this.reticle.lookAt(this.kit.camera.position);
    this.reticle.scale.setScalar(1 + beat * 0.25);
  }

  private createBeam(start: THREE.Vector3, end: THREE.Vector3, color: number): void {
    const geo = new THREE.BufferGeometry().setFromPoints([start.clone(), end.clone()]);
    const line = new THREE.Line(
      geo,
      new THREE.LineBasicMaterial({
        color,
        transparent: true,
        opacity: 0.95,
      }),
    );
    this.kit.scene.add(line);
    this.beams.push({ line, life: 0.08 });
  }

  private fireShot(colorBoost: number): void {
    this.raycaster.setFromCamera(this.tmpVec2.set(this.aimX, this.aimY), this.kit.camera);
    const ray = this.raycaster.ray;
    let hit: Enemy | null = null;
    let hitDistance = Number.POSITIVE_INFINITY;
    const hitPoint = this.tmpVec3B;

    for (const enemy of this.enemies) {
      this.tmpSphere.center.copy(enemy.group.position);
      this.tmpSphere.radius = enemy.radius;
      const intersection = ray.intersectSphere(this.tmpSphere, new THREE.Vector3());
      if (!intersection) continue;
      const distance = intersection.distanceToSquared(ray.origin);
      if (distance < hitDistance) {
        hitDistance = distance;
        hit = enemy;
        hitPoint.copy(intersection);
      }
    }

    const beamColor = colorBoost > 0.55 ? 0xfff18c : 0xffc4fb;
    const beamEnd = hit
      ? hitPoint.clone()
      : ray.origin.clone().add(ray.direction.clone().multiplyScalar(44));
    this.createBeam(ray.origin.clone(), beamEnd, beamColor);
    playShoot();

    if (!hit) {
      if (this.combo > 0) this.combo = Math.max(0, this.combo - 1);
      return;
    }

    hit.hp -= 1;
    this.particles.emit(hit.group.position.x, hit.group.position.y, hit.group.position.z, this.kit.lowPower ? 8 : 14, 7);
    if (hit.hp > 0) return;

    hit.group.visible = false;
    this.combo = Math.min(12, this.combo + 1);
    this.comboTimer = 1.8;
    const gained = Math.round(hit.score * (1 + Math.max(0, this.combo - 1) * 0.35));
    this.score += gained;
    this.message = this.combo > 1 ? `COMBO x${this.combo}` : `+${gained}`;
    this.messageT = 0.8;
    if (this.combo > 0 && this.combo % 5 === 0) playPowerup();
    playExplosion();
  }

  private takeHit(message: string, damage: number): void {
    if (this.invuln > 0 || this.lives <= 0) return;
    this.lives = Math.max(0, this.lives - damage);
    this.combo = 0;
    this.comboTimer = 0;
    this.invuln = 1.25;
    this.message = this.lives > 0 ? message : 'FEED LOCKED — FIRE TO RESTART';
    this.messageT = this.lives > 0 ? 1 : 99;
    if (typeof navigator !== 'undefined' && navigator.vibrate) {
      navigator.vibrate(this.lives > 0 ? [12, 26, 12] : [24, 60, 24]);
    }
  }

  private reset(): void {
    this.score = 0;
    this.lives = 4;
    this.wave = 1;
    this.combo = 0;
    this.comboTimer = 0;
    this.message = 'BREAK THE GATEKEEPERS';
    this.messageT = 1.5;
    this.invuln = 0;
    this.fireCd = 0;
    this.spawnCd = 0.45;
    this.burstCd = 0.9;
    this.distance = 0;
    this.aimX = 0;
    this.aimY = 0;
    this.pointerAimX = 0;
    this.pointerAimY = 0;
    this.pointerRecent = 0;
    this.pointerFire = false;
    this.pointerFirePressed = false;
    this.pointerId = null;
    this.camX = 0;
    this.camY = 1.8;

    for (const enemy of this.enemies) {
      this.kit.root.remove(enemy.group);
      disposeObject(enemy.group);
    }
    this.enemies = [];

    for (const hazard of this.hazards) {
      this.kit.root.remove(hazard.mesh);
      disposeObject(hazard.mesh);
    }
    this.hazards = [];

    for (const beam of this.beams) {
      this.kit.scene.remove(beam.line);
      disposeObject(beam.line);
    }
    this.beams = [];
  }

  update(dt: number, input: InputState): void {
    if (this.disposed) return;

    const { energy, bass, highs, beat, kick } = this.ctx.getBands();
    const speed = 13 + this.wave * 0.7 + energy * 7 + beat * 4;
    this.distance += dt * speed;
    this.wave = Math.min(18, 1 + Math.floor(this.score / 750));
    this.messageT = Math.max(0, this.messageT - dt);
    this.invuln = Math.max(0, this.invuln - dt);
    this.fireCd = Math.max(0, this.fireCd - dt);
    this.spawnCd = Math.max(0, this.spawnCd - dt);
    this.burstCd = Math.max(0, this.burstCd - dt);
    this.comboTimer = Math.max(0, this.comboTimer - dt);
    if (this.comboTimer <= 0) this.combo = 0;

    const firePressed = input.firePressed || this.pointerFirePressed;
    const fireHeld = input.fire || this.pointerFire;
    this.pointerFirePressed = false;

    if (this.lives <= 0) {
      if (firePressed) this.reset();
      this.updateCamera(dt, energy, kick);
      this.updateCorridor(dt, speed * 0.35, beat, highs);
      this.refreshReticle(beat);
      this.particles.update(dt);
      renderThree(this.kit);
      return;
    }

    this.updateAim(dt, input);
    this.updateCamera(dt, energy, kick);
    this.updateCorridor(dt, speed, beat, highs);
    this.refreshReticle(beat);

    if ((firePressed || fireHeld) && this.fireCd <= 0) {
      this.fireShot(highs + bass);
      this.fireCd = Math.max(0.08, 0.18 - highs * 0.05 - bass * 0.04);
      if (typeof navigator !== 'undefined' && navigator.vibrate) navigator.vibrate(4);
    }

    if (this.spawnCd <= 0) {
      this.spawnEnemy(energy, bass);
      if (beat > 0.55 || energy > 0.72) this.spawnEnemy(energy, bass);
      this.spawnCd = Math.max(0.22, 0.8 - energy * 0.28 - this.wave * 0.025);
    }
    if (this.burstCd <= 0 && bass > 0.58) {
      this.spawnEnemy(0.9, bass);
      if (!this.kit.lowPower) this.spawnEnemy(0.85, bass);
      this.burstCd = 1.6 - Math.min(0.8, bass * 0.7);
    }

    for (const enemy of this.enemies) {
      enemy.z -= dt * speed * enemy.speedMul;
      enemy.phase += dt * (1.8 + highs * 1.5);
      const sway = Math.sin(enemy.phase * (enemy.kind === 'algo' ? 1.8 : 1.1) + enemy.lane) * 0.7;
      const bob = Math.sin(enemy.phase * 2.4) * 0.25;
      enemy.group.position.set(enemy.lane * 2.45 + sway, enemy.height + bob, enemy.z);
      enemy.group.rotation.y += dt * (enemy.kind === 'paywall' ? 0.45 : 1.8);
      enemy.group.rotation.x = Math.sin(enemy.phase) * 0.08;

      if (enemy.z < enemy.shotAt && enemy.z > 4.5) {
        this.spawnHazard(enemy, speed);
        enemy.shotAt = -99;
      }

      if (enemy.z <= 1.3) {
        enemy.group.visible = false;
        this.takeHit('ACCESS DENIED', enemy.kind === 'paywall' ? 2 : 1);
      }
    }
    this.enemies = this.enemies.filter((enemy) => {
      if (!enemy.group.visible || enemy.z < -3) {
        this.kit.root.remove(enemy.group);
        disposeObject(enemy.group);
        return false;
      }
      return true;
    });

    const playerX = this.camX;
    const playerY = 1.3;
    for (const hazard of this.hazards) {
      hazard.x += hazard.vx * dt;
      hazard.y += hazard.vy * dt;
      hazard.z += hazard.vz * dt;
      hazard.mesh.position.set(hazard.x, hazard.y, hazard.z);
      hazard.mesh.rotation.x += dt * 1.8;
      hazard.mesh.rotation.y += dt * 2.1;

      const dz = hazard.z - 0.6;
      const distSq =
        (hazard.x - playerX) * (hazard.x - playerX) +
        (hazard.y - playerY) * (hazard.y - playerY) +
        dz * dz;
      if (distSq < hazard.radius * hazard.radius * 2.3) {
        (hazard.mesh as THREE.Mesh).visible = false;
        this.takeHit('FILTER BURST', hazard.damage);
      } else if (hazard.z < -1.8) {
        (hazard.mesh as THREE.Mesh).visible = false;
      }
    }
    this.hazards = this.hazards.filter((hazard) => {
      if (!(hazard.mesh as THREE.Mesh).visible) {
        this.kit.root.remove(hazard.mesh);
        disposeObject(hazard.mesh);
        return false;
      }
      return true;
    });

    for (const beam of this.beams) {
      beam.life -= dt;
      const mat = beam.line.material as THREE.LineBasicMaterial;
      mat.opacity = Math.max(0, beam.life * 11);
    }
    this.beams = this.beams.filter((beam) => {
      if (beam.life <= 0) {
        this.kit.scene.remove(beam.line);
        disposeObject(beam.line);
        return false;
      }
      return true;
    });

    this.particles.update(dt);
    this.score += Math.floor(dt * (10 + energy * 12 + Math.min(5, this.combo)));
    renderThree(this.kit);
  }

  resize(w: number, h: number): void {
    if (!this.kit) return;
    resizeThree(this.kit, w, h, 20, 25);
  }

  dispose(): void {
    this.disposed = true;
    const el = this.kit?.renderer.domElement;
    if (el) {
      el.removeEventListener('pointerdown', this.onPointerDown);
      el.removeEventListener('pointermove', this.onPointerMove);
      el.removeEventListener('pointerup', this.onPointerUp);
      el.removeEventListener('pointercancel', this.onPointerUp);
    }
    for (const beam of this.beams) disposeObject(beam.line);
    disposeObject(this.stars);
    disposeObject(this.reticle);
    disposeObject(this.kit.root);
    this.beams = [];
    this.enemies = [];
    this.hazards = [];
    this.corridor = [];
    this.kit?.dispose();
  }

  getHud() {
    const comboHud = this.combo > 1 ? `COMBO x${this.combo}` : '';
    return {
      score: this.score,
      lives: this.lives,
      wave: this.wave,
      message:
        this.messageT > 0
          ? this.message
          : this.lives <= 0
            ? 'FIRE TO RESTART'
            : comboHud || 'POINTER AIM · D-PAD BACKUP',
    };
  }
}
